import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as ExecutionsService from '../services/ExecutionsService';
import {Execution} from '../types';
import {notifyError, notifySuccess} from '../../../components/PlatformNotification';

// Page size is a SERVER concern under token pagination: it is the `limit` sent
// with the query, fixed by the module. There is no rows-per-page control,
// because a token model has no page to resize and no total to divide.
const DEFAULT_LIMIT = 10;

// Query keys
export const executionsKeys = {
    all: ['executions'] as const,
    lists: () => [...executionsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...executionsKeys.lists(), {filters}] as const,
    details: () => [...executionsKeys.all, 'detail'] as const,
    detail: (id: string) => [...executionsKeys.details(), id] as const,
};

// Hooks
export const useExecutionsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        // `filters` is part of the key, so changing a filter fetches rather
        // than silently serving the previous filter's cached pages.
        queryKey: executionsKeys.list(filters),
        queryFn: ({pageParam}: { pageParam: string | null }) =>
            ExecutionsService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        // MUST return `undefined` — not null, and never the token just used —
        // when there are no more pages. Returning either makes React Query
        // believe another page exists and refetch forever.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const useExecutionQuery = (id: string) => {
    return useQuery({
        queryKey: executionsKeys.detail(id),
        queryFn: () => ExecutionsService.fetch(id),
        enabled: !!id,
    });
};

export const useInvalidateExecutions = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({queryKey: executionsKeys.all});
};

export const useStartExecutionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ExecutionsService.start,
        onSuccess: (execution) => {
            notifySuccess('Execution started');
            queryClient.invalidateQueries({queryKey: executionsKeys.lists()});
            if (execution?.id) {
                queryClient.setQueryData(executionsKeys.detail(execution.id), execution);
            }
        },
        onError: notifyError,
    });
};

export const useCancelExecutionMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ExecutionsService.cancel,
        onSuccess: (execution) => {
            notifySuccess('Execution cancellation requested');
            queryClient.invalidateQueries({queryKey: executionsKeys.lists()});
            if (execution?.id) {
                queryClient.setQueryData<Execution | undefined>(
                    executionsKeys.detail(execution.id),
                    (current) => {
                        if (!current) return execution;
                        return {...current, ...execution, phases: current.phases};
                    },
                );
            }
        },
        onError: notifyError,
    });
};
